import { makeAutoObservable, runInAction } from "mobx";
import { ApiResponse, IRootStore, OrderStatus } from "../index.d";
import Api from "../../services/api";
import { ApiConfig } from "../../services/api-config";
import { IOrder, IUpdateOrderStatusResponse } from ".";
import { IStripeOrderRefundResponse, IStripeSessionResponse } from "../cart";

export default class OrderStore {
  orders: Array<IOrder> = [];
  viewOrder: IOrder | null = null;

  private rootStore: IRootStore;
  private paymentStatusRequests = new Map<string, Promise<ApiResponse<IStripeSessionResponse> | undefined>>();

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "paymentStatusRequests">(this, { paymentStatusRequests: false });
  }

  fetchOrders = (onLoaded?: (orders: IOrder[]) => void, background = false) => {
    const url = ApiConfig.REST_URIS.order.allOrders;
    const get = background ? Api.getSilent.bind(Api) : Api.get.bind(Api);
    return get(url, (res: ApiResponse<IOrder[]>) => {
      if (res?.isSuccess && res.data) {
        onLoaded?.(res.data);
        runInAction(() => { this.orders = res.data; });
      }
    });
  };

  receiveNewOrders = (order: IOrder) => {
    const index = this.orders.findIndex((o) => o.orderId === order.orderId);
    if (index > -1) {
      this.orders.splice(index, 1, order);
    } else {
      this.orders.unshift(order);
    }

    this.orders = [...this.orders];
  };

  // fetchNewOrders = () => {
  //   this.isNewOrdersReceived = false;
  //   const url = ApiConfig.REST_URIS.order.allOrders;

  //   Api.getSilent(url, (res: ApiResponse<IOrder[]>) => {
  //     if (res?.isSuccess && res.data) {
  //       if (this.orders.length < res.data.length) {
  //         this.orders = res.data;
  //         this.isNewOrdersReceived = true;
  //       } else {
  //         const newOrdersEx = this.orders.filter(
  //           (p) => p.orderStatus == OrderStatus.Placed
  //         );
  //         const newOrders = res.data.filter(
  //           (p) => p.orderStatus == OrderStatus.Placed
  //         );
  //         this.isNewOrdersReceived = newOrdersEx.some(
  //           (o) =>
  //             o.orderStatus !=
  //             newOrders.find((p) => p.orderId === o.orderId)?.orderStatus
  //         );
  //         if (this.isNewOrdersReceived) this.orders = res.data;
  //       }
  //     }
  //   });
  // };

  fetchCustomerOrders = (userId: string, background = false) => {
    const url = ApiConfig.REST_URIS.order.customerOrders(userId);
    const get = background ? Api.getSilent.bind(Api) : Api.get.bind(Api);
    return get(url, (res: ApiResponse<IOrder[]>) => {
      if (res?.isSuccess && res.data) {
        runInAction(() => { this.orders = res.data; });
      }
    });
  };

  fetchOrderById = (id: number) => {
    const url = ApiConfig.REST_URIS.order.orderById(id);
    Api.get(url, (res: ApiResponse<IOrder>) => {
      if (res?.isSuccess && res.data) {
        this.viewOrder = res.data;
      }
    });
  };

  updateOrderStatus = (
    id: number,
    statusId: OrderStatus,
    callback: Function
  ) => {
    Api.put(
      ApiConfig.REST_URIS.order.updateOrderStatus(id, statusId),
      {},
      (res: ApiResponse<IUpdateOrderStatusResponse>) => {
        callback && callback(res?.isSuccess, res?.data, statusId);
      }
    );
  };

  markAsPaymentPaid = (id: number, callback: Function) => {
    Api.put(
      ApiConfig.REST_URIS.order.markAsPaymentPaid(id),
      {},
      (res: ApiResponse<number>) => {
        callback && callback(res?.isSuccess, res?.data);
      }
    );
  };

  takePayment = (id: number, callback: Function) => {
    Api.post(
      ApiConfig.REST_URIS.order.takePayment(id),
      {},
      (res: ApiResponse<IStripeSessionResponse>) => {
        callback && callback(res);
      }
    );
  };

  refreshPaymentStatus = (
    orderId: number, id: string,
    callback?: (response: ApiResponse<IStripeSessionResponse> | undefined) => void
  ): Promise<ApiResponse<IStripeSessionResponse> | undefined> => {
    const key = `${orderId}:${id}`;
    let request = this.paymentStatusRequests.get(key);
    if (!request) {
      const url = ApiConfig.REST_URIS.order.refreshPaymentStatus(orderId, id);
      request = (async () => {
        let result: ApiResponse<IStripeSessionResponse> | undefined;
        // Background polling must not flash the global loading overlay. A
        // timeout lets a stalled connection recover on the next polling cycle.
        await Api.getSilent(url, (res: ApiResponse<IStripeSessionResponse>) => { result = res; }, 20000);
        return result;
      })();
      this.paymentStatusRequests.set(key, request);
      void request.finally(() => this.paymentStatusRequests.delete(key)).catch(() => {});
    }
    return request.then(response => {
      callback?.(response);
      return response;
    });
  };

  createRefund = (
    orderId: number,
    requestedFullRefund: boolean,
    requestedRefundAmount: number,
    callback: Function,
    requestKey?: string
  ) => {
    Api.post(
      ApiConfig.REST_URIS.order.createRefund,
      { orderId, requestedFullRefund, requestedRefundAmount, requestKey },
      (res: ApiResponse<IStripeOrderRefundResponse>) => {
        callback && callback(!!res?.isSuccess, res?.data);
      }
    );
  };

  refreshRefundStatus = (orderId: number, callback?: Function) => {
    const url = ApiConfig.REST_URIS.order.refreshRefundStatus(orderId);
    Api.get(url, (res: ApiResponse<IStripeOrderRefundResponse>) => {
      callback && callback(!!res?.isSuccess, res?.data);
    });
  };

  assignDeliveryDriver = (
    orderId: number,
    deriverId: number,
    callback: Function
  ) => {
    if (deriverId === 0) {
      this.updateOrderStatus(orderId, OrderStatus.OutForDelivery, callback);
      return;
    }

    const url = ApiConfig.REST_URIS.order.assignDeliveryDriver(
      orderId,
      deriverId
    );
    Api.put(url, {}, (res: ApiResponse<number>) => {
      callback && callback(res.isSuccess, res.data);
    });
  };

  updateOrderPrintStatus = (orderId: number, callback: Function) => {
    const url = ApiConfig.REST_URIS.order.updateOrderPrintStatus(orderId);
    Api.put(url, {}, (res: ApiResponse<boolean>) => {
      if (res?.isSuccess && res.data) {
        const order = this.orders.find((item) => item.orderId === orderId);
        if (order) {
          order.lastPrintedDate = new Date().toISOString();
          this.orders = [...this.orders];
        }
      }
      callback && callback(!!res?.isSuccess, res?.data);
    });
  };
}
